import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Input, Button, Spin, Card, Row, Col } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import L from 'leaflet';
import { useGeocoding } from 'packages/utils/hooks';
import { createAddressMapMarkerIcon } from '@src/components/AddressMapViewer/addressMapMarkerIcon';
import { LIGHT_TILE_LAYER } from '@src/components/Maps/constants';
import GeocodingService from '@src/services/GeocodingService';
import styles from './styles.module.less';
import { MapResizer } from './components/MapResizer';
import { MapClickHandler } from './components/MapClickHandler';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

/** Map viewport when no address is selected (Santiago, Chile). */
const DEFAULT_MAP_CENTER = { lat: -33.4489, lon: -70.6693 };

const parsePosition = position => {
  if (!position || typeof position.lat !== 'number') return null;
  const lon = position.lon ?? position.lng;
  if (typeof lon !== 'number') return null;
  return { lat: position.lat, lon };
};

const AddressMapPicker = ({
  onAddressSelect,
  initialPosition,
  zoom = 13,
  visible = true,
  includeMyLocation = false,
  customHeight = '400px',
  showSearch = true,
  /** When false, initial / prop-driven position updates only update the map UI, not `onAddressSelect`. */
  notifyParentOnInitialLoad = true,
}) => {
  const [position, setPosition] = useState(() => parsePosition(initialPosition));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loadingReverse, setLoadingReverse] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const mapRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const customMarkerIcon = useMemo(() => createAddressMapMarkerIcon(), []);
  const { searchLocations, locations, loading, getAddressFromCoords } = useGeocoding();

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  }, [onAddressSelect]);

  useEffect(() => {
    let cancelled = false;
    const parsed = parsePosition(initialPosition);

    if (!parsed) {
      setPosition(null);
      setSelectedAddress(null);
      setSearchQuery('');
      if (mapRef.current) {
        mapRef.current.setView([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lon], zoom);
      }
      return undefined;
    }

    const { lat, lon } = parsed;
    setPosition(parsed);
    if (mapRef.current) mapRef.current.setView([lat, lon], zoom);

    setLoadingReverse(true);
    getAddressFromCoords(lat, lon)
      .then(addressData => {
        if (cancelled) return;
        const resolved = addressData || GeocodingService.formatCoordinatesFallback(lat, lon);

        setSelectedAddress(resolved);
        setSearchQuery(resolved.label);

        if (!notifyParentOnInitialLoad) return;
        const select = onAddressSelectRef.current;
        if (!select) return;

        select({
          ...resolved.address,
          latitude: lat,
          longitude: lon,
          fullAddress: resolved.label,
        });
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error getting address from initial position:', error);
        const resolved = GeocodingService.formatCoordinatesFallback(lat, lon);
        setSelectedAddress(resolved);
        setSearchQuery(resolved.label);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingReverse(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    initialPosition?.lat,
    initialPosition?.lon,
    initialPosition?.lng,
    notifyParentOnInitialLoad,
    zoom,
    getAddressFromCoords,
  ]);

  const handleSearch = useCallback(
    value => {
      setSearchQuery(value);
      if (value.length > 2) {
        searchLocations(value);
        setShowResults(true);
      } else {
        setShowResults(false);
      }
    },
    [searchLocations],
  );

  const handleSelectFromSearch = useCallback(
    async loc => {
      const newPos = { lat: loc.lat, lon: loc.lon };
      setPosition(newPos);
      setSelectedAddress(loc);
      setSearchQuery(loc.label);
      setShowResults(false);

      if (mapRef.current) mapRef.current.setView([loc.lat, loc.lon], 16);
      if (!onAddressSelect) return;

      onAddressSelect({
        ...loc.address,
        latitude: loc.lat,
        longitude: loc.lon,
        fullAddress: loc.label,
      });
    },
    [onAddressSelect],
  );

  const handleMapClick = useCallback(
    async (lat, lon) => {
      setPosition({ lat, lon });
      setLoadingReverse(true);

      try {
        const addressData = await getAddressFromCoords(lat, lon);
        const resolved = addressData || GeocodingService.formatCoordinatesFallback(lat, lon);

        setSelectedAddress(resolved);
        setSearchQuery(resolved.label);
        if (!onAddressSelect) return;

        onAddressSelect({
          ...resolved.address,
          latitude: lat,
          longitude: lon,
          fullAddress: resolved.label,
        });
      } catch (error) {
        console.error('Error getting address:', error);
        const resolved = GeocodingService.formatCoordinatesFallback(lat, lon);
        setSelectedAddress(resolved);
        setSearchQuery(resolved.label);
        if (onAddressSelect) {
          onAddressSelect({
            ...resolved.address,
            latitude: lat,
            longitude: lon,
            fullAddress: resolved.label,
          });
        }
      } finally {
        setLoadingReverse(false);
      }
    },
    [getAddressFromCoords, onAddressSelect],
  );

  const handleMarkerDrag = e => {
    const { lat, lng } = e.target.getLatLng();
    handleMapClick(lat, lng);
  };

  const handleUseMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude } = position.coords;
        handleMapClick(latitude, longitude);

        if (mapRef.current) mapRef.current.setView([latitude, longitude], 16);
      });
    }
  }, [handleMapClick]);

  return (
    <div className={styles.container}>
      <Row gutter={[16, 16]}>
        {showSearch && (
          <Col span={24}>
            <div className={styles.searchWrapper}>
              <Input.Search
                className={styles.searchInput}
                placeholder="Buscar dirección..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                loading={loading}
                size="large"
                prefix={<EnvironmentOutlined />}
                suffix={
                  includeMyLocation && (
                    <Button
                      type="text"
                      icon={<AimOutlined />}
                      onClick={handleUseMyLocation}
                      title="Usar mi ubicación"
                      size="small"
                    />
                  )
                }
              />

              {showResults && locations.length > 0 && (
                <div className={styles.searchResults}>
                  {locations.map((location, index) => (
                    <div
                      key={index}
                      className={styles.searchResultItem}
                      onClick={() => handleSelectFromSearch(location)}
                    >
                      <EnvironmentOutlined /> {location.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        )}

        <Col span={24}>
          <div className={styles.mapWrapper}>
            {loadingReverse && (
              <div className={styles.loadingOverlay}>
                <Spin tip="Obteniendo dirección..." />
              </div>
            )}

            <MapContainer
              center={[
                position?.lat ?? DEFAULT_MAP_CENTER.lat,
                position?.lon ?? DEFAULT_MAP_CENTER.lon,
              ]}
              zoom={zoom}
              style={{ height: customHeight, width: '100%' }}
            >
              <TileLayer
                attribution={LIGHT_TILE_LAYER.attribution}
                url={LIGHT_TILE_LAYER.url}
                subdomains={LIGHT_TILE_LAYER.subdomains}
                maxZoom={LIGHT_TILE_LAYER.maxZoom}
              />

              <MapResizer mapRef={mapRef} visible={visible} />
              <MapClickHandler onLocationSelect={handleMapClick} />

              {position && (
                <Marker
                  position={[position.lat, position.lon]}
                  icon={customMarkerIcon}
                  draggable={true}
                  eventHandlers={{
                    dragend: handleMarkerDrag,
                  }}
                />
              )}
            </MapContainer>
          </div>
        </Col>

        {selectedAddress && position && (
          <Col span={24}>
            <Card size="small" type="inner" className="gx-mb-1">
              <strong>Dirección seleccionada:</strong>
              <div>{selectedAddress.label}</div>
              <div className={styles.coordinates}>
                📍 {position.lat.toFixed(6)}, {position.lon.toFixed(6)}
              </div>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

AddressMapPicker.propTypes = {
  onAddressSelect: PropTypes.func.isRequired,
  initialPosition: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  zoom: PropTypes.number,
  visible: PropTypes.bool,
  includeMyLocation: PropTypes.bool,
  customHeight: PropTypes.string,
  notifyParentOnInitialLoad: PropTypes.bool,
};

export default AddressMapPicker;
